/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'primereact/panel';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import AssessmentTemplates from '../../../components/accountInvitation/AssessmentTemplates';

const messages = defineMessages({
  header: { id: 'accounts.invitation-detail.panel-header', defaultMessage: 'Invitation review' },
  fullName: { id: 'attrs.fullName', defaultMessage: 'Full Name' },
  email: { id: 'attrs.email', defaultMessage: 'Email' },
  message: { id: 'attrs.message', defaultMessage: 'Message: ' },
  plan: { id: 'attrs.credit-plan', defaultMessage: 'Account Credit Plan' },
});

const AssessmentDimensions = ({ sections }) => {
  if (!sections) return null;

  const { behaviorSection, cognitiveSection, interestsSection } = sections;

  const hasBehaviors = behaviorSection !== null && behaviorSection.dimensions && behaviorSection.dimensions.length > 0;
  const hasCognitive =
    cognitiveSection !== null && cognitiveSection.dimensions && cognitiveSection.dimensions.length > 0;
  const hasInterests =
    interestsSection !== null && interestsSection.dimensions && interestsSection.dimensions.length > 0;

  const shouldDisplay = hasBehaviors && hasCognitive && hasInterests;

  if (!shouldDisplay) {
    return null;
  }

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.invitation-detail.assessment-dimensions.heading"
          defaultMessage="Assessment Dimensions"
          tagName="strong"
        />
      </div>
      {hasBehaviors && (
        <>
          <div className="p-col-12">
            <strong>{behaviorSection.displayName}</strong>
            <ul>
              {behaviorSection.dimensions.map((d) => (
                <li key={d.id}>{d.displayName}</li>
              ))}
            </ul>
          </div>

          <div className="p-col-12">
            <FormattedMessage
              id="accounts.invitation-detail.assessment-dimensions.distortion-title"
              defaultMessage="Distortion"
              tagName="strong"
            />
            <ul>
              {behaviorSection.distortionDimensions.map((d) => (
                <li key={d.id}>{d.displayName}</li>
              ))}
            </ul>
          </div>
        </>
      )}
      {hasCognitive && (
        <div className="p-col-12">
          <strong>{cognitiveSection.displayName}</strong>
          <ul>
            {cognitiveSection.dimensions.map((d) => (
              <li key={d.id}>{d.displayName}</li>
            ))}
          </ul>
        </div>
      )}
      {hasInterests && (
        <div className="p-col-12">
          <strong>{interestsSection.displayName}</strong>
          <ul>
            {interestsSection.dimensions.map((d) => (
              <li key={d.id}>{d.displayName}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

AssessmentDimensions.propTypes = {
  sections: PropTypes.objectOf(PropTypes.any).isRequired,
};

const SurveyTemplates = ({ surveyTemplates }) => {
  const shouldDisplay = surveyTemplates.length > 0;

  if (!shouldDisplay) {
    return null;
  }

  return (
    <>
      <FormattedMessage
        id="accounts.invitation-detail.survey-templates.heading"
        defaultMessage="Survey Templates"
        tagName="strong"
      />
      <ul>
        {surveyTemplates.map(({ id, title }) => (
          <li key={id}>{title}</li>
        ))}
      </ul>
    </>
  );
};

SurveyTemplates.propTypes = {
  surveyTemplates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const SurveyAbilities = ({ surveyCompetencies }) => {
  const hasCompetencies = surveyCompetencies && surveyCompetencies.length > 0;

  if (!hasCompetencies) {
    return null;
  }

  return (
    <>
      <FormattedMessage
        id="accounts.invitation-detail.survey-competencies.heading"
        defaultMessage="Survey Abilities"
        tagName="strong"
      />
      <ul>
        {surveyCompetencies.map((compentecy) => (
          <li key={compentecy.id}>
            <strong>{compentecy.displayName}</strong>
            <ul>
              {compentecy.abilities.map((a) => (
                <li key={a.id}>{a.displayName}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </>
  );
};

SurveyAbilities.propTypes = {
  surveyCompetencies: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const AccountInvitationDetail = ({ data }) => {
  const { formatMessage } = useIntl();

  const { account } = data;

  const { companyInformation, pendingClientInvitation } = account;

  const message = formatMessage(messages.header);

  return (
    <div className="p-grid brand-detailed-wrapper">
      <div className="p-col-12">
        <Panel header={message}>
          <div className="p-col-12">
            <FormattedMessage
              id="accounts.add-client.steps.client.details"
              defaultMessage="The new account will be created under {partnerName}"
              values={{
                partnerName: <strong>{companyInformation.displayName}</strong>,
              }}
            />
          </div>
          <div className="p-col-12">
            <div className="details">
              <span className="details-title">{`${formatMessage(messages.fullName)}: `}</span>
              <span>{pendingClientInvitation.fullName}</span>
            </div>
          </div>
          <div className="p-col-12">
            <div className="details">
              <span className="details-title">{`${formatMessage(messages.email)}: `}</span>
              <span>{pendingClientInvitation.email}</span>
            </div>
          </div>
          <div className="p-col-12">
            <div className="details">
              <span className="details-title">{formatMessage(messages.message)}</span>
              <div className="formatted-text">{pendingClientInvitation.inviteMessage}</div>
            </div>
          </div>
          <div className="p-col-12">
            <AssessmentTemplates assessmentTemplates={pendingClientInvitation.assessmentTemplates} />
          </div>
          <div className="p-col-12">
            <AssessmentDimensions sections={pendingClientInvitation.assessmentSections} />
          </div>
          <div className="p-col-12">
            <SurveyTemplates surveyTemplates={pendingClientInvitation.surveyTemplates} />
          </div>
          <div className="p-col-12">
            <SurveyAbilities surveyCompetencies={pendingClientInvitation.surveyCompetencies} />
          </div>
        </Panel>
      </div>
    </div>
  );
};

AccountInvitationDetail.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default AccountInvitationDetail;
